import React, { useState } from "react";
import { Box, Button, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useThemeContext } from "../../../Context/ThemeContext";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ProductModal from "./ProductModal";
import { updateSlot } from "../../../Services/authService";
import SuccessNotification from "../../../Components/Common/SuccessNotification";
import TempImage from '../../../assets/TempProductImage.webp'
import TempImage1 from '../../../assets/ProductImage.webp'
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import './VendingMachines.css'
import { ToolTipIcon } from "../../../Components/Layouts/CustomIcons";

const SlotCard = ({ slot, refreshSlots, tags, isSelected, onSelect, machineNumber }) => {
    const { t } = useTranslation();
    const { mode } = useThemeContext();
    const { slotName, product, quantity, capacity } = slot;
    const [modalOpen, setModalOpen] = useState(false);
    const [error, setError] = useState(""); // State to store error messages
    const [successNotification, setSuccessNotification] = useState(false);

    const handleModalOpen = () => setModalOpen(true);
    const handleModalClose = () => setModalOpen(false);

    const handleSave = async (data, slotId) => {
        try {
            // Clear any previous error
            setError("");
            await updateSlot(slotId, data);
            handleModalClose();
            setSuccessNotification(true)
            // Add delay to show the notification before navigating
            setTimeout(() => {
                if (refreshSlots) {
                    refreshSlots();
                }
                // Uncheck the slot after saving
                onSelect(slotId, false); // Uncheck the slot
            }, 2000); // 3-second delay
        } catch (error) {
            console.error(t("vendingMachines.failedUpdateSlot"), error);
            setError(error.detail || t("vendingMachines.errorUpdatingSlotMessage"));
        }
    };

    const backgroundCard = mode === 'dark' ? DARKCOLORS.BackgroundCard : LIGHTCOLORS.BackgroundCard;
    const inputBoxColor = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;
    const dotsColor = mode === 'dark' ? DARKCOLORS.GreyScale70 : LIGHTCOLORS.GreyScale70;
    const textborderColor = mode === 'dark' ? DARKCOLORS.GreyScale30 : LIGHTCOLORS.GreyScale30;
    const digitsColor = mode === 'dark' ? DARKCOLORS.BodyLightBackground : LIGHTCOLORS.BodyLightBackground;
    const badgeColor = mode === 'dark' ? DARKCOLORS.Warning500 : LIGHTCOLORS.Warning500;
    const badgeColor1 = mode === 'dark' ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;

    // Function to get the status badge style
    const getStatusBadge = (quantity, capacity) => {
        if (quantity === 0) {
            return (
                <Box
                    sx={{
                        position: "absolute",
                        top: "14px",
                        left: "5px",
                        backgroundColor: badgeColor1,
                        color: "#FFFFFF",
                        padding: "9px 25px 8px",
                        borderRadius: "0px 4px 4px 0px",
                        fontSize: "10px",
                    }}
                >
                    {t('vendingMachines.empty')}
                </Box>
            );
        } else if (quantity < capacity / 2) {
            return (
                <Box
                    sx={{
                        position: "absolute",
                        top: "14px",
                        left: "5px",
                        backgroundColor: badgeColor,
                        color: "#FFFFFF",
                        padding: "9px 25px 8px",
                        borderRadius: "0px 4px 4px 0px",
                        fontSize: "10px",
                    }}
                >
                    {t('vendingMachines.restock')}
                </Box>
            );
        }
        return null;
    };

    // Function to get the tag icon based on the label
    const getTagIcon = (tagLabel) => {
        const tag = tags.find(tag => tag.label.toLowerCase() === tagLabel.toLowerCase());
        if (!tag || !tag.IconComponent) return null;

        const Icon = tag.IconComponent;
        const noSlash = ['vegan', 'vegetarian', 'high protein'].includes(tagLabel.toLowerCase());

        return (
            <span
                style={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 35,
                    height: 35,
                    borderRadius: '50%',
                    padding: '5px',
                    backgroundColor: 'white', // or use theme
                    overflow: 'hidden',
                }}
            >
                <Icon width="20" height="20" />

                {!noSlash && (
                    <span
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            width: '100%',
                            height: '1px',
                            backgroundColor: 'black', // Adjust to contrast with background
                            transform: 'translate(-50%, -50%) rotate(130deg)',
                            transformOrigin: 'center',
                        }}
                    />
                )}
            </span>
        );
    };

    return (
        <>
            <Box sx={{ position: "relative" }}>
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onSelect(slot.slotId)}
                    style={{
                        position: "absolute",
                        top: "10px",
                        left: "10px",
                        zIndex: 15,
                        width: "18px",
                        height: "18px",
                        cursor: "pointer"
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
            </Box>
            <Box
                onClick={handleModalOpen}
                sx={{
                    background: inputBoxColor,
                    borderRadius: "16px 16px 8px 8px",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                }}
            >
                {/* Product Image */}
                <Box sx={{ position: "relative" }}>
                    <img
                        src={product.productName ? (product.imageUrl ? product.imageUrl : TempImage1) : TempImage}
                        alt={product.productName}
                        style={{
                            width: "calc(100% - 10px)", // Subtract padding to fit the container with the gap
                            height: "200px",
                            objectFit: "cover",
                            margin: "5px 5px 0px",
                            borderRadius: "8px", // Same as card's radius
                        }}
                    />

                    {/* Slot Name (e.g., A1, A2) */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: "10px",
                            right: "10px",
                            backgroundColor: backgroundCard,
                            borderRadius: "50%",
                            width: "30px",
                            height: "30px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "12px",
                            zIndex: 10,
                        }}
                    >
                        {slotName.toUpperCase()}
                    </Box>

                    {/* Status Badge */}
                    {getStatusBadge(quantity, capacity)}

                    {/* Tags: Icons at the bottom-right corner */}
                    {product?.tags && product?.tags?.length > 0 && (
                        <Box
                            sx={{
                                position: "absolute",
                                bottom: "10px",
                                right: "10px",
                                display: "flex",
                                gap: "5px",
                            }}
                        >
                            {product?.tags.map((tag, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        // backgroundColor: "#fff",
                                        // borderRadius: "50%",
                                        // padding: "5px",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >
                                    {getTagIcon(tag)}
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Slot Details */}
                <Box sx={{ padding: "5px 10px" }}>
                    {/* Product Name */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem", gap: "10px" }}>
                        <p className="machine-name-text">{product.productName}</p>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {/* ⬅ Show alert icon only if error exists */}
                            {slot.errorMessage && (
                                <Tooltip title={slot.errorMessage} arrow>
                                    <span>
                                        <ToolTipIcon
                                            sx={{ color: badgeColor1, cursor: "pointer" }}
                                            fontSize="small"
                                        />
                                    </span>
                                </Tooltip>
                            )}
                            <MoreVertIcon sx={{ color: dotsColor }} fontSize="sm" />
                        </Box>
                    </Box>

                    {/* Quantity */}
                    <Box sx={{
                        display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem", gap: "10px",
                        border: `0.5px solid ${textborderColor}`,
                        borderRadius: "6px",
                        padding: "10px 14px",
                    }}>
                        <p className="venidng-sec-card-text">{t('vendingMachines.items')}</p>
                        <p className="popup-text" style={{ margin: "0px", color: digitsColor }}>{quantity} / {capacity}</p>
                    </Box>

                    {/* Price */}
                    <Box sx={{
                        display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem", gap: "10px",
                        background: backgroundCard,
                        borderRadius: "6px",
                        padding: "10px 14px",
                    }}>
                        <p className="venidng-sec-card-text">{t('vendingMachines.price')}</p>
                        <p className="popup-text" style={{ margin: "0px", color: digitsColor }}>
                            {product.actualPrice === product.discountedPrice ? (
                                `€${product.actualPrice.toFixed(2)}`
                            ) : (
                                <>
                                    <s style={{ color: "gray", marginRight: "8px", fontSize: "12px" }}>
                                        €{product.actualPrice.toFixed(2)}
                                    </s>
                                    €{product.discountedPrice.toFixed(2)}
                                </>
                            )}
                        </p>
                    </Box>
                </Box>
            </Box>

            {/* Modal */}
            <ProductModal
                open={modalOpen}
                onClose={handleModalClose}
                slot={slot}
                error={error}
                onSave={handleSave}
                tags={tags}
                refreshSlots={refreshSlots}
                machineNumber={machineNumber}
            />

            {/* Use Notification Component */}
            <SuccessNotification
                open={successNotification}
                message={t('membershipCards.successMessage')}
                severity="success"
                onClose={() => setSuccessNotification(false)}
            />
        </>
    );
};

export default SlotCard;
